import { API_BASE_URL } from '../../api/client';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useNotifications } from '../../notifications/useNotifications';
import { Logo } from '../../brand/Logo';
import { Button } from '../ui';
import './AppShell.css';

export interface AppShellProps {
  children: ReactNode;
  /** Rendered at the top of the content area, above the page body. */
  header?: ReactNode;
}

/**
 * The frame every page sits in.
 *
 * One header, one navigation, one footer — so the product reads as a single
 * application rather than a set of screens that happen to share colours.
 *
 * Two things here are for people not using a mouse, and both were missing until
 * Phase 27 went looking. The navigation has grown an entry every few phases and
 * is now ten links; a keyboard reader met all ten before the content of every
 * page, and a phone reader met three rows of them — about a fifth of the
 * screen — before the first word. So: a skip link, and a navigation that
 * collapses behind a button when the screen is narrow.
 */
export function AppShell({ children, header }: AppShellProps) {
  const { account, signOut } = useAuth();
  const { pathname } = useLocation();
  const navId = useId();
  const [navOpen, setNavOpen] = useState(false);

  // Going somewhere closes it. A menu still standing open over the page you
  // just asked for is a menu you have to dismiss before you can read anything.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);
  // Asked once per session rather than on a timer, and asked again by whatever
  // changes it: a badge that polls costs a request a minute for the rest of
  // the session, and one that never asks again tells people a number that is
  // no longer true.
  const { unread } = useNotifications();

  return (
    <div className="sc-shell">
      {/* The first thing a keyboard reaches, invisible until it is focused. */}
      <a className="sc-shell__skip" href="#sc-content">
        Skip to the content
      </a>

      <header className="sc-shell__bar">
        <Link to="/" className="sc-shell__brand" aria-label="Simple Coffee home">
          <Logo />
        </Link>

        <button
          type="button"
          className="sc-shell__menu"
          aria-expanded={navOpen}
          aria-controls={navId}
          onClick={() => setNavOpen((open) => !open)}
        >
          <span aria-hidden="true">{navOpen ? '✕' : '☰'}</span>
          <span className="sc-visually-hidden">
            {navOpen ? 'Close the menu' : 'Open the menu'}
            {/* The count in words, here as well as on the link. At phone width
                the link is `display: none`, which hides it from a screen reader
                too — so without this the number would be announced nowhere on
                the surface that most needs it. */}
            {!navOpen && unread > 0 ? `, ${unread} unread` : ''}
          </span>
          {/* The badge follows the button when the links are hidden, so an
              unread count is not something you have to open a menu to learn. */}
          {!navOpen && unread > 0 && (
            <span className="sc-shell__badge" aria-hidden="true">
              {unread}
            </span>
          )}
        </button>

        <nav
          id={navId}
          className={`sc-shell__nav${navOpen ? ' sc-shell__nav--open' : ''}`}
          aria-label="Main"
        >
          <NavLink to="/discover" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Discover
          </NavLink>
          <NavLink to="/search" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Search
          </NavLink>
          <NavLink to="/datasets" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Beans
          </NavLink>
          {account && (
            <NavLink to="/dashboards" className={({ isActive }) => (isActive ? 'is-active' : '')}>
              Dashboards
            </NavLink>
          )}
          {account && (
            <NavLink to="/stories" className={({ isActive }) => (isActive ? 'is-active' : '')}>
              Stories
            </NavLink>
          )}
          {account && (
            <NavLink to="/saved" className={({ isActive }) => (isActive ? 'is-active' : '')}>
              Saved
            </NavLink>
          )}
          {(account?.role === 'MODERATOR' || account?.role === 'ADMIN') && (
            <NavLink to="/moderation" className={({ isActive }) => (isActive ? 'is-active' : '')}>
              Review
            </NavLink>
          )}
          {account?.role === 'ADMIN' && (
            <NavLink to="/admin" className={({ isActive }) => (isActive ? 'is-active' : '')}>
              Admin
            </NavLink>
          )}
          {account ? (
            <>
              <NavLink
                to="/notifications"
                className={({ isActive }) => (isActive ? 'is-active' : '')}
              >
                Notifications
                {unread > 0 && (
                  <span className="sc-shell__badge" aria-hidden="true">
                    {unread}
                  </span>
                )}
                {unread > 0 && <span className="sc-visually-hidden">, {unread} unread</span>}
              </NavLink>
              <NavLink to="/account" className={({ isActive }) => (isActive ? 'is-active' : '')}>
                @{account.handle}
              </NavLink>
              <Button size="sm" variant="secondary" onClick={() => void signOut()}>
                Sign out
              </Button>
            </>
          ) : (
            <>
              <NavLink to="/login">Sign in</NavLink>
              <Link to="/register">
                <Button size="sm">Create account</Button>
              </Link>
            </>
          )}
        </nav>
      </header>

      <main className="sc-shell__main" id="sc-content" tabIndex={-1}>
        {header}
        {children}
      </main>

      <footer className="sc-shell__footer">
        <span>Simple Coffee · v0.1.0</span>
        {/* The API is a stated contract since Phase 26, so it has a way in from
            the product rather than only from a README somebody has to find. */}
        <a href={`${API_BASE_URL}/docs`} target="_blank" rel="noreferrer">
          API
        </a>
      </footer>
    </div>
  );
}
